/// <reference types="vite/client" />
import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
// @ts-ignore
import { ReactCipher } from "@repo/utils";
import { API_ENDPOINTS } from "@repo/constants";

/* ----------------------------------------------------
   Base URL Helpers (Optimized & Clean)
---------------------------------------------------- */

const normalizeUrl = (url?: string) => {
  if (!url) return "http://localhost:8000";
  return url.trim().replace(/\/+$/, "");
};

const BASE_URL = normalizeUrl(
  import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL,
);

const BASE_URL_2 = normalizeUrl(import.meta.env.VITE_BASE_URL_2);

/* ----------------------------------------------------
   Token Memory Cache (Avoid localStorage hit each time)
---------------------------------------------------- */

export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("OneDeskToken", token);
  } else {
    localStorage.removeItem("OneDeskToken");
  }
};

/* ----------------------------------------------------
   Axios Instances
---------------------------------------------------- */

const createApiClient = (baseURL: string): AxiosInstance => {
  return axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const apiClient = createApiClient(BASE_URL);
export const channelApiClient = createApiClient(BASE_URL_2);

/* ----------------------------------------------------
   Encryption Skip Routes (Faster than includes)
---------------------------------------------------- */

const SKIP_ENCRYPTION = new Set([
  API_ENDPOINTS.uploadImage,
  "channelPartner/insert",
  "channelPartner/update",
  "channelPartner/list"
]);

const shouldSkipEncryption = (url?: string) => {
  if (!url) return false;
  return Array.from(SKIP_ENCRYPTION).some((endpoint) =>
    url.includes(endpoint),
  );
};

/* ----------------------------------------------------
   Interceptors Setup
---------------------------------------------------- */

const setupInterceptors = (instance: AxiosInstance) => {
  /* -------------------- REQUEST -------------------- */
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      /* Attach token from localStorage */
      const token = localStorage.getItem("OneDeskToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      /* Skip encryption if required */
      if (
        shouldSkipEncryption(config.url) ||
        config.data instanceof FormData ||
        config.data?.payload
      ) {
        return config;
      }

      /* Encrypt Payload */
      if (config.data) {
        try {
          const encrypted = ReactCipher.encrypt(
            JSON.stringify(config.data),
          );
          if (encrypted) {
            config.data = { payload: encrypted };
          }
        } catch (err) {
          console.error("Encryption failed:", err);
        }
      }

      return config;
    },
    (error: any) => Promise.reject(error),
  );

  /* -------------------- RESPONSE -------------------- */
  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (error.response?.status === 401) {
        setAuthToken(null);

        // Emit event for auth system
        window.dispatchEvent(new Event("unauthorized"));
        
        if (!window.location.pathname.includes("/desklogin")) {
          window.location.href = "/desklogin";
        }
      }

      return Promise.reject(error);
    },
  );
};

setupInterceptors(apiClient);
setupInterceptors(channelApiClient);

/* ----------------------------------------------------
   Optional: Abort Controller Support (Advanced)
---------------------------------------------------- */

export const createCancelableRequest = () => {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    cancel: () => controller.abort(),
  };
};

// /// <reference types="vite/client" />
// import axios from "axios";
// // @ts-ignore
// import { ReactCipher } from "@repo/utils";
// import { API_ENDPOINTS } from "@repo/constants";

// // Create axios instance with default config
// const getBaseUrl = () => {
//   let url =
//     import.meta.env.VITE_API_BASE_URL ||
//     import.meta.env.VITE_BASE_URL ||
//     "http://localhost:8000";
//   // Trim potential whitespace
//   url = url.trim();
//   // Remove trailing slash
//   if (url.endsWith("/")) url = url.slice(0, -1);
//   return url;
// };

// const getBaseUrl2 = () => {
//   let url = import.meta.env.VITE_BASE_URL_2 || "http://localhost:8000";
//   // Trim potential whitespace
//   url = url.trim();
//   // Remove trailing slash
//   if (url.endsWith("/")) url = url.slice(0, -1);
//   return url;
// };

// export const apiClient = axios.create({
//   baseURL: getBaseUrl(),
//   timeout: 30000,
//   headers: {
//     "Content-Type": "application/json",
//   },
// });

// export const channelApiClient = axios.create({
//   baseURL: getBaseUrl2(),
//   timeout: 30000,
//   headers: {
//     "Content-Type": "application/json",
//   },
// });

// const setupInterceptors = (instance: any) => {
//   // Request interceptor
//   instance.interceptors.request.use(
//     async (config: any) => {
//       const token = localStorage.getItem("OneDeskToken");
//       if (token) {
//         config.headers.Authorization = `Bearer ${token}`;
//       }
//       if (
//         config.url === API_ENDPOINTS.uploadImage ||
//         config.url?.includes("/fileUpload/uploadImage") ||
//         config.url?.includes("channelPartner/insert") ||
//         config.url?.includes("channelPartner/update") ||
//         config.url?.includes("channelPartner/list")
//       ) {
//         return config; // Skip encryption
//       }

//       // Standard encryption for other requests
//       if (
//         config.data &&
//         !(config.data instanceof FormData) &&
//         !config.data.payload
//       ) {
//         const encrypted = ReactCipher.encrypt(JSON.stringify(config.data));
//         if (encrypted) {
//           config.data = { payload: encrypted };
//         }
//       }

//       return config;
//     },
//     (error: any) => Promise.reject(error),
//   );

//   // Response interceptor
//   instance.interceptors.response.use(
//     (response: any) => response,
//     (error: any) => {
//       // Handle common errors
//       if (
//         error.response?.status === 401 &&
//         !window.location.pathname.includes("/login")
//       ) {
//         localStorage.removeItem("OneDeskToken");
//         window.location.href = "/login";
//       }
//       return Promise.reject(error);
//     },
//   );
// };

// setupInterceptors(apiClient);
// setupInterceptors(channelApiClient);
